import { Box } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { DS } from "../ds/Kit";

type CardSpendStatusRatioProps = {
  successful: number;
  declined: number;
  successRate: number;
  declineRate: number;
  pending?: number;
  incomplete?: boolean;
  /** Opens the matching transactions; cells become buttons when provided. */
  onSelect?: (bucket: "successful" | "declined" | "pending") => void;
};

const PERCENTAGE_MAX = 100;

const STATUS_TONES = {
  success: { dot: "#10B981", bg: "#ECFDF5", fg: "#047857" },
  danger: { dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C" },
  warning: { dot: "#F59E0B", bg: "#FFFBEB", fg: "#B45309" },
} as const;

/** One cell of the joined status strip: dot + label, the count, and its share as a soft pill. */
const StatusMetric = ({
  tone,
  label,
  value,
  detail,
  onClick,
}: {
  tone: keyof typeof STATUS_TONES;
  label: string;
  value: number;
  detail: string;
  onClick?: () => void;
}) => (
  <Box
    role="group"
    aria-label={`${label}: ${value}, ${detail}`}
    sx={{ position: "relative", minWidth: 0, padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px", transition: "background-color .15s ease", "&:hover": onClick ? { backgroundColor: DS.soft } : {} }}
  >
    {onClick && (
      <Box
        element="button"
        type="button"
        aria-label={`View ${label.toLowerCase()} transactions`}
        onClick={onClick}
        sx={{ position: "absolute", inset: 0, width: "100%", border: 0, padding: 0, background: "transparent", cursor: "pointer", "&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" } }}
      />
    )}
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
      <Box aria-hidden sx={{ width: "8px", height: "8px", flexShrink: 0, borderRadius: "50%", backgroundColor: STATUS_TONES[tone].dot }} />
      <Typo sx={{ color: DS.muted, fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Typo>
    </Box>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
      <Typo sx={{ color: DS.ink, fontSize: "24px", lineHeight: 1.1, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</Typo>
      <Box element="span" sx={{ padding: "2px 8px", borderRadius: "999px", backgroundColor: STATUS_TONES[tone].bg, color: STATUS_TONES[tone].fg, fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" }}>
        {detail}
      </Box>
    </Box>
  </Box>
);

const clampPercentage = (value: number) =>
  Number.isFinite(value) ? Math.min(PERCENTAGE_MAX, Math.max(0, value)) : 0;

export const CardSpendStatusRatio = ({
  successful,
  declined,
  successRate,
  declineRate,
  pending = 0,
  incomplete = false,
  onSelect,
}: CardSpendStatusRatioProps) => {
  const finalizedTotal = successful + declined;
  const successfulShare =
    finalizedTotal > 0
      ? clampPercentage((successful / finalizedTotal) * PERCENTAGE_MAX)
      : 0;
  const declinedShare =
    finalizedTotal > 0
      ? clampPercentage((declined / finalizedTotal) * PERCENTAGE_MAX)
      : 0;

  return (
    <Box
      role="region"
      aria-label="Successful versus declined card-spend transactions"
      sx={{ marginBottom: "4px" }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          marginBottom: "14px",
          borderRadius: "14px",
          border: `1px solid ${DS.line}`,
          backgroundColor: DS.surface,
          overflow: "hidden",
          "& > * + *": { borderLeft: `1px solid ${DS.line}` },
        }}
      >
        <StatusMetric
          tone="success"
          label={incomplete ? "Loaded successful" : "Successful"}
          value={successful}
          detail={`${successRate.toFixed(1)}%`}
          onClick={onSelect && (() => onSelect("successful"))}
        />
        <StatusMetric
          tone="danger"
          label={incomplete ? "Loaded declined" : "Declined"}
          value={declined}
          detail={`${declineRate.toFixed(1)}%`}
          onClick={onSelect && (() => onSelect("declined"))}
        />
        <StatusMetric
          tone="warning"
          label={incomplete ? "Loaded pending" : "Pending"}
          value={pending}
          detail="Excluded from %"
          onClick={onSelect && (() => onSelect("pending"))}
        />
      </Box>

      {finalizedTotal === 0 ? (
        <Typo variant="caption" sx={{ display: "block", color: DS.muted, fontSize: "13px" }}>
          No completed card payments in this period.
        </Typo>
      ) : (
        <>
      <Box
        role="img"
        aria-label={`Successful ${successRate.toFixed(1)}%, declined ${declineRate.toFixed(1)}%`}
        sx={{
          display: "flex",
          width: "100%",
          height: "10px",
          overflow: "hidden",
          border: `1px solid ${DS.line}`,
          borderRadius: "999px",
          backgroundColor: "#F1F5F9",
        }}
      >
        <Box
          aria-label="Successful share"
          sx={{
            width: `${successfulShare}%`,
            height: "100%",
            backgroundColor: "#34D399",
            transition: "width 200ms ease",
          }}
        />
        <Box
          aria-label="Declined share"
          sx={{
            width: `${declinedShare}%`,
            height: "100%",
            backgroundColor: "#F87171",
            transition: "width 200ms ease",
          }}
        />
      </Box>
      <Typo
        variant="caption"
        sx={{ display: "block", color: DS.faint, marginTop: "8px", fontSize: "12px" }}
      >
        Percentages exclude pending transactions.
      </Typo>
        </>
      )}
    </Box>
  );
};
